import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/solid';
import { Auth } from "aws-amplify";
import Message from "../../../components/message/message-comp";
import logo from '../../../assets/images/logo/logo.png';
import TextBox from "../../../components/forms/text-box";
import Loader from "../../../components/form-loader/form-loader";

function ForgotPasswordPage() {
    const navigate = useNavigate();
    const [auth, setAuth] = useState({
        username: '',
        verifyCode: '',
        password: '',
    });

    const [resetState, setResetState] = useState();

    const [error, setError] = useState({
        username: false,
        verifyCode: false,
        password: false
    });

    const [formState, setFormState] = useState({
        processing: false,
        success: false,
        failed: false,
        message: ''
    });

    const handleChange = useCallback(event => {
        const targetName = event.target.name;
        const targetValue = event.target.value;

        if (targetName === 'username')
            auth.username = targetValue;

        if (targetName === 'verifyCode')
            auth.verifyCode = targetValue;

        if (targetName === 'password')
            auth.password = targetValue;

        setAuth(Object.assign({}, auth));
    });

    const validateForm = () => {
        let valid = true;
        if (auth.username === '') {
            error.username = true;
            valid = false;
        } else {
            error.username = false;
        }

        setError(Object.assign({}, error))
        return valid;
    }

    const validateVerifyForm = () => {
        let valid = true;
        if (auth.verifyCode === '') {
            error.verifyCode = true;
            valid = false;
        } else {
            error.verifyCode = false;
        }

        if (auth.password === '') {
            error.password = true;
            valid = false;
        } else {
            error.password = false;
        }

        setError(Object.assign({}, error))
        return valid;
    }

    const updateFormState = (processing, success, failed, message) => {
        setFormState(Object.assign({}, { processing, success, failed, message }))
    }

    const handleSubmit = useCallback(async (event) => {
        event.preventDefault();
        if (!formState.processing && validateForm()) {
            updateFormState(true, false, false);
            setResetState(null)
            try {
                const resp = await Auth.forgotPassword(auth.username);
                updateFormState(false, true, false);
                setResetState(resp);
            } catch (err) {
                console.error("reset pwd failed cause", err);
                updateFormState(false, false, true, err.message);
            }
        }
    });

    const handleSetNewPassword = useCallback(async (event) => {
        event.preventDefault();
        if (!formState.processing && validateVerifyForm()) {
            updateFormState(true, false, false);
            try {
                await Auth.forgotPasswordSubmit(auth.username, auth.verifyCode, auth.password);
                updateFormState(false, true, false);
                navigate('/signin');
            } catch (err) {
                console.error("reset pwd failed cause", err);
                updateFormState(false, false, true);
            }
        }
    });

    return (
        <div className="h-screen bg-white flex justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <Link to="/">
                        <img
                            className="mx-auto h-16 w-auto"
                            src={logo}
                            alt="BotCircuits"
                        />
                    </Link>
                    <h2 className="mt-6 text-center text-3xl font-semibold text-gray-900">Reset your password</h2>
                </div>

                {resetState &&
                    <div className="flex justify-center items-center">
                        <Message text={`We have sent verify code to your ${resetState.CodeDeliveryDetails.Destination} email`} type="success" />
                    </div>
                }

                {formState.failed &&
                    <div className="flex justify-center items-center">
                        <Message
                            text={formState.message ? formState.message : 'Reset password failed. Please check your details'}
                            type="error" />
                    </div>
                }

                {formState.processing &&
                    <div className="flex justify-center items-center">
                        <Loader />
                    </div>
                }
                <div className="sm:mx-auto sm:w-full sm:max-w-sm">
                    <div className="bg-white border border-slate-200 py-6 px-6 shadow-lg rounded-lg">
                        <form className="space-y-4" onSubmit={handleSubmit}>
                            <input type="hidden" name="remember" defaultValue="true" />
                            <TextBox
                                id="username"
                                name="username"
                                label="Email address"
                                value={auth.username}
                                onChange={handleChange}
                                type="email"
                                autoComplete="email"
                                placeholder="Email address"
                                error={error.username}
                            />

                            {resetState &&
                                <TextBox
                                    id="verifyCode"
                                    name="verifyCode"
                                    label="Verify Code"
                                    value={auth.verifyCode}
                                    onChange={handleChange}
                                    type="text"
                                    placeholder="Verify Code"
                                    error={error.verifyCode}
                                />
                            }

                            {resetState &&
                                <TextBox
                                    id="password"
                                    name="password"
                                    label="New Password"
                                    type="password"
                                    value={auth.password}
                                    onChange={handleChange}
                                    autoComplete="current-password"
                                    placeholder="New Password"
                                    error={error.password}
                                />
                            }

                            {!resetState &&
                                <div className="pt-3">
                                    <button
                                        type="submit"
                                        className="flex w-full justify-center rounded-md border border-transparent bg-primary py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                    >
                                        <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                            <LockClosedIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-300" aria-hidden="true" />
                                        </span>
                                        Reset password
                                    </button>
                                </div>
                            }

                            {resetState &&
                                <div className="pt-3">
                                    <button
                                        onClick={handleSetNewPassword}
                                        type="button"
                                        className="flex w-full justify-center rounded-md border border-transparent bg-primary py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 relative"
                                    >
                                        <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                            <LockClosedIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-300" aria-hidden="true" />
                                        </span>
                                        Set new password
                                    </button>
                                </div>
                            }

                            {resetState &&
                                <div className="pt-2">
                                    <button
                                        onClick={handleSubmit}
                                        type="button"
                                        className="flex w-full justify-center rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-primary shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                    >
                                        Resend Verification Code
                                    </button>
                                </div>
                            }

                            <div className="flex items-center justify-between pt-2">
                                <div className="text-sm">
                                    <Link to="/signin" className="font-medium text-primary hover:text-secondary">
                                        Sign in to my account
                                    </Link>
                                </div>
                                <div className="text-sm">
                                    <Link to="/signup" className="font-medium text-primary hover:text-secondary">
                                        Create new account
                                    </Link>
                                </div>
                            </div>

                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ForgotPasswordPage;
